/**
 * Canonical bone tree (left column in RIG / MAP / ANIMATE).
 *
 * The tree is always the same 18 canonical bones — an imported skeleton is only ever *mapped*
 * onto it — so the rows are structural and only the annotation column changes per mode:
 *   MAP      a status dot (mapped / uncertain / error / unmapped); the row's `title` carries the
 *            source bone name, which is far too long to sit in a 280px row
 *   ANIMATE  ● key at the current frame, ○ keyed somewhere, blank otherwise
 *   RIG      the row doubles as the joint list (click also selects the joint to move)
 *
 * The panel fills its column and scrolls inside itself (`layout: 'fill'` + `section({fill})`), so
 * the blocks above and the footer below it stay put however long the list is.
 *
 * Selection/hover highlighting repaints classes in place instead of rebuilding, so dragging the
 * mouse down the list does not churn the DOM (and does not fight the viewport's own picking).
 */

import { CANONICAL_BONE_IDS, CANONICAL_PARENT } from '../../shared';
import type { CanonicalBoneId } from '../../shared';
import type { EditorContext } from '../context';
import type { Controllers } from '../controllers/types';
import type { EditorState } from '../index';
import { hasKeyAt, selectBoneKeyframes } from '../index';
import { el, icon } from './dom';
import { createPanel, section, type PanelHandle } from './panel';

function depthOf(bone: CanonicalBoneId): number {
  let depth = 0;
  let parent = CANONICAL_PARENT[bone];
  while (parent) {
    depth++;
    parent = CANONICAL_PARENT[parent];
  }
  return depth;
}

function isBoneId(value: string | undefined): value is CanonicalBoneId {
  return value !== undefined && (CANONICAL_BONE_IDS as readonly string[]).includes(value);
}

/**
 * MAP status of one bone. An error outranks the confidence: a row the validation rejected is the
 * one the user has to act on, whatever the auto-mapper thought of it.
 */
function mapDotClass(state: EditorState, bone: CanonicalBoneId): 'ok' | 'warn' | 'bad' | 'none' {
  const hasError = (state.mapping.validation?.errors ?? []).some((issue) => issue.bone === bone);
  if (hasError) return 'bad';
  if (!state.mapping.draft[bone]) return 'none';
  return state.mapping.confidence[bone] === 'uncertain' ? 'warn' : 'ok';
}

export function mountHierarchyPanel(
  container: HTMLElement,
  ctx: EditorContext,
  _controllers?: Controllers,
): PanelHandle {
  const { store } = ctx;
  const rows = new Map<CanonicalBoneId, HTMLElement>();

  function render(root: HTMLElement): void {
    const state = store.getState();
    rows.clear();

    let mapped = 0;
    const list = el('div', { class: 'tree' });
    for (const bone of CANONICAL_BONE_IDS) {
      let annotation: HTMLElement | null = null;
      let title: string | null = null;
      if (state.mode === 'map') {
        const source = state.mapping.draft[bone];
        if (source) mapped++;
        title = source ?? '—';
        annotation = el('span', { class: 'ann' }, [
          el('span', { class: `dot ${mapDotClass(state, bone)}` }),
        ]);
      } else if (state.mode === 'animate') {
        const frame = Math.round(state.playhead.frame);
        let mark = '';
        if (hasKeyAt(state, bone, frame)) mark = '●';
        else if (selectBoneKeyframes(state, bone).length > 0) mark = '○';
        if (mark) annotation = el('span', { class: 'ann', text: mark });
      }

      const depth = depthOf(bone);
      const row = el(
        'div',
        {
          class: 'row',
          'data-bone': bone,
          style: `padding-left:${4 + depth * 12}px`,
          title,
        },
        [icon(depth === 0 ? 'joint' : 'jointSmall', 12), el('span', { class: 'bone', text: bone }), annotation],
      );
      rows.set(bone, row);
      list.appendChild(row);
    }
    root.appendChild(
      section('Outliner', [list], {
        // In MAP the count IS the progress of the mode; everywhere else the list is structural.
        meta:
          state.mode === 'map'
            ? `${mapped}/${CANONICAL_BONE_IDS.length} mapped`
            : `${CANONICAL_BONE_IDS.length} joints`,
        flush: true,
        icon: 'layers',
        fill: true,
      }),
    );
    paint();
  }

  /** Highlight only — safe to run synchronously from a store listener. */
  function paint(): void {
    const state = store.getState();
    for (const [bone, row] of rows) {
      row.classList.toggle('sel', state.selection.bone === bone);
      row.classList.toggle('hov', state.hover === bone);
      row.classList.toggle('joint', state.mode === 'rig' && state.rig.selectedJoint === bone);
    }
  }

  const instance = createPanel(container, ctx, render, {
    className: 'hierarchy',
    // `fill`: the outliner takes the column's leftover height and scrolls its own body.
    layout: 'fill',
    slices: [
      (s) => s.mode,
      (s) => s.mapping.draft,
      (s) => s.mapping.confidence,
      (s) => s.mapping.validation,
      (s) => s.clip,
      (s) => Math.round(s.playhead.frame),
    ],
  });

  // Delegated: the rows are replaced on every rebuild, the root is not.
  const boneOf = (event: Event): CanonicalBoneId | null => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return null;
    const row = target.closest('[data-bone]');
    if (!(row instanceof HTMLElement)) return null;
    return isBoneId(row.dataset.bone) ? row.dataset.bone : null;
  };

  instance.root.addEventListener('click', (event) => {
    const bone = boneOf(event);
    if (!bone) return;
    store.actions.selectBone(bone);
    if (store.getState().mode === 'rig') store.actions.selectJoint(bone);
  });
  instance.root.addEventListener('mouseover', (event) => {
    const bone = boneOf(event);
    if (bone) store.actions.hoverBone(bone);
  });
  instance.root.addEventListener('mouseleave', () => store.actions.hoverBone(null));

  instance.own(store.select((s) => s.selection.bone, paint));
  instance.own(store.select((s) => s.hover, paint));
  instance.own(store.select((s) => s.rig.selectedJoint, paint));

  return instance;
}
